/**
 * Converts an object to a URL query string without encoding the parameter names or values.
 *
 * @param {object} data - The object to convert to a query string.
 * @param {string} [prefix=""] - Optional prefix to use for each parameter name.
 * @returns {string} The resulting URL query string without encoding.
 */
const createQueryString = (data, prefix = "") => {
    let queryParams = [];

    // Loop over each key-value pair in the object
    for (const [key, value] of Object.entries(data)) {
        // Skip undefined or null values
        if (value === undefined || value === null) {
            continue;
        }

        const finalKey = prefix ? `${prefix}[${key}]` : key;

        if (value && typeof value === "object" && !Array.isArray(value)) {
            // Recursively process nested objects
            queryParams.push(createQueryString(value, finalKey));
        } else if (Array.isArray(value)) {
            // Handle array values by serializing them as [index]
            value.forEach((item, index) => {
                // Skip undefined or null values in arrays
                if (item === undefined || item === null) {
                    return;
                }

                if (typeof item === "object") {
                    // If the item is an object, serialize its properties
                    Object.entries(item).forEach(([itemKey, itemValue]) => {
                        const arrayKey = `${finalKey}[${index}][${itemKey}]`;
                        queryParams.push(`${arrayKey}=${itemValue}`); // No encoding
                    });
                } else {
                    // If it's a simple value, just append it normally
                    const arrayKey = `${finalKey}[${index}]`;
                    queryParams.push(`${arrayKey}=${item}`); // No encoding
                }
            });
        } else {
            // For simple values, add them directly without encoding
            queryParams.push(`${finalKey}=${value}`); // No encoding
        }
    }

    return queryParams.join("&");
};

/**
 * Appends an object as query parameters to the given relative URL,
 * preserving any existing query parameters in the input URL.
 *
 * @param {string} url - The relative URL (e.g., "/general/products?category=پاکت-حمل").
 * @param {object} data - The data object to be converted into a query string.
 * @param {string} [prefix=""] - Optional prefix for the query string keys.
 * @returns {string} - The modified URL with the appended query string.
 */
const ObjectToURL = (url, data, prefix = "") => {
    if (!data || Object.keys(data).length === 0) return url;  // If no data is provided or the object is empty, return the original URL

    // Split the URL into base path and existing query string
    const [path, existingQueryString] = url.split("?");
    const existingParams = new URLSearchParams(existingQueryString || "");

    // Generate the new query string from the provided data
    const newQueryParams = createQueryString(data, prefix);

    // Append new query parameters to the existing parameters
    newQueryParams.split("&").forEach(param => {
        const [key, value] = param.split("=");
        existingParams.set(key, value);  // Use set() to overwrite existing keys with the same name
    });

    // Construct the final URL with updated query string
    const finalQueryString = existingParams.toString();

    // Return the final URL with query parameters (if any)
    return finalQueryString ? `${path}?${finalQueryString}` : path;
};

export default ObjectToURL;
